import CryptoJS from 'crypto-js';

/**
 * 文件工具类
 * 提供文件相关的工具函数
 */
export class FileUtils {
  /**
   * 将File对象转换为MD5哈希值
   * @param file 文件对象
   * @returns Promise<string> MD5哈希值
   */
  static async fileToMD5(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();

      reader.onload = (event) => {
        try {
          const arrayBuffer = event.target?.result as ArrayBuffer;
          const wordArray = CryptoJS.lib.WordArray.create(arrayBuffer);
          const md5Hash = CryptoJS.MD5(wordArray).toString();
          resolve(md5Hash);
        } catch (error) {
          reject(new Error('MD5计算失败'));
        }
      };

      reader.onerror = () => {
        reject(new Error('文件读取失败'));
      };

      reader.readAsArrayBuffer(file);
    });
  }

  /**
   * 将文件内容字符串转换为MD5哈希值
   * @param content 文件内容字符串
   * @returns string MD5哈希值
   */
  static stringToMD5(content: string): string {
    return CryptoJS.MD5(content).toString();
  }

  /**
   * 将Blob对象转换为MD5哈希值
   * @param blob Blob对象
   * @returns Promise<string> MD5哈希值
   */
  static async blobToMD5(blob: Blob): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();

      reader.onload = (event) => {
        try {
          const arrayBuffer = event.target?.result as ArrayBuffer;
          const wordArray = CryptoJS.lib.WordArray.create(arrayBuffer);
          const md5Hash = CryptoJS.MD5(wordArray).toString();
          resolve(md5Hash);
        } catch (error) {
          reject(new Error('MD5计算失败'));
        }
      };

      reader.onerror = () => {
        reject(new Error('Blob读取失败'));
      };

      reader.readAsArrayBuffer(blob);
    });
  }

  /**
   * 批量计算文件的MD5哈希值
   * @param files 文件数组
   * @returns Promise<Array<{file: File, md5: string}>> 文件及其MD5哈希值的数组
   */
  static async batchFilesToMD5(files: File[]): Promise<Array<{ file: File; md5: string }>> {
    const promises = files.map(async (file) => {
      const md5 = await this.fileToMD5(file);
      return { file, md5 };
    });

    return Promise.all(promises);
  }

  /**
   * 检查文件是否已存在（通过MD5比较）
   * @param newFile 新文件
   * @param existingFiles 已存在的文件数组
   * @returns Promise<boolean> 是否存在重复文件
   */
  static async checkFileExists(newFile: File, existingFiles: File[]): Promise<boolean> {
    try {
      const newFileMD5 = await this.fileToMD5(newFile);
      const existingMD5s = await Promise.all(existingFiles.map((file) => this.fileToMD5(file)));

      return existingMD5s.includes(newFileMD5);
    } catch (error) {
      console.error('文件MD5比较失败:', error);
      return false;
    }
  }

  /**
   * 获取文件的基本信息
   * @param file 文件对象
   * @returns 文件信息对象
   */
  static getFileInfo(file: File) {
    return {
      name: file.name,
      size: file.size,
      type: file.type,
      lastModified: file.lastModified,
      extension: file.name.split('.').pop()?.toLowerCase() || '',
    };
  }

  /**
   * 格式化文件大小
   * @param bytes 字节数
   * @returns 格式化后的文件大小字符串
   */
  static formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 Bytes';

    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));

    return `${parseFloat((bytes / k ** i).toFixed(2))} ${sizes[i]}`;
  }

  /**
   * 处理COS上传返回的Location，提取文件名
   * @param location COS返回的Location字符串
   * @returns 处理后的文件名
   */
  static processCOSLocation(location: string): string {
    if (!location) return '';

    // 从Location中提取 /var/ 或 /video/ 后面的部分
    const locationMatch = location.match(/\/(var|video)\/(.+)$/);
    if (locationMatch) {
      return locationMatch[2]; // 获取文件名部分
    }

    // 如果没有匹配到，则返回完整的Location
    return location;
  }

  /**
   * 从COS Location中提取路径信息
   * @param location COS返回的Location字符串
   * @returns 包含路径类型和文件名的对象
   */
  static extractCOSPathInfo(location: string): {
    pathType: string;
    fileName: string;
    fullPath: string;
  } {
    if (!location) {
      return { pathType: '', fileName: '', fullPath: '' };
    }

    const locationMatch = location.match(/\/(var|video)\/(.+)$/);
    if (locationMatch) {
      return {
        pathType: locationMatch[1], // var 或 video
        fileName: locationMatch[2], // 文件名
        fullPath: locationMatch[0], // 完整路径 /var/filename 或 /video/filename
      };
    }

    return {
      pathType: '',
      fileName: location,
      fullPath: location,
    };
  }

  /**
   * 验证COS上传响应数据
   * @param data COS上传返回的数据
   * @returns 验证结果和错误信息
   */
  static validateCOSResponse(data: any): { isValid: boolean; error?: string } {
    if (!data) {
      return { isValid: false, error: '上传响应数据为空' };
    }

    if (!data.Location) {
      return { isValid: false, error: '缺少Location信息' };
    }

    if (!data.ETag) {
      return { isValid: false, error: '缺少ETag信息' };
    }

    return { isValid: true };
  }
}

/**
 * 文件MD5计算Hook
 * 提供React组件中使用的文件MD5计算功能
 */
export const useFileMD5 = () => {
  /**
   * 计算单个文件的MD5
   * @param file 文件对象
   * @returns Promise<string> MD5哈希值
   */
  const calculateFileMD5 = async (file: File): Promise<string> => {
    return FileUtils.fileToMD5(file);
  };

  /**
   * 批量计算文件的MD5
   * @param files 文件数组
   * @returns Promise<Array<{file: File, md5: string}>> 文件及其MD5哈希值的数组
   */
  const calculateBatchFilesMD5 = async (
    files: File[],
  ): Promise<Array<{ file: File; md5: string }>> => {
    return FileUtils.batchFilesToMD5(files);
  };

  /**
   * 检查文件是否重复
   * @param newFile 新文件
   * @param existingFiles 已存在的文件数组
   * @returns Promise<boolean> 是否存在重复文件
   */
  const checkFileDuplicate = async (newFile: File, existingFiles: File[]): Promise<boolean> => {
    return FileUtils.checkFileExists(newFile, existingFiles);
  };

  return {
    calculateFileMD5,
    calculateBatchFilesMD5,
    checkFileDuplicate,
  };
};
